import { createContext, useContext, useEffect, useState, useCallback } from "react";

const CartContext = createContext(null);
const KEY = "eyw_cart";

const lineKey = (productId, variantName) => `${productId}::${variantName || ""}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  const add = useCallback((product, qty = 1, variant = null) => {
    const vname = variant?.name || "";
    const key = lineKey(product.id, vname);
    setItems((prev) => {
      const found = prev.find((i) => i.key === key);
      if (found) return prev.map((i) => i.key === key ? { ...i, qty: i.qty + qty } : i);
      return [...prev, {
        key, product_id: product.id, title: product.title, type: product.type,
        price: product.price, price_delta: Number(variant?.price_delta || 0),
        variant: vname, member_discount_pct: product.member_discount_pct,
        image: product.image, shipping_price: product.shipping_price, qty,
      }];
    });
    setOpen(true);
  }, []);

  const setQty = useCallback((key, qty) => {
    setItems((prev) => prev.map((i) => i.key === key ? { ...i, qty: Math.max(1, qty) } : i));
  }, []);
  const addPackage = useCallback((pkg) => {
    const key = `pkg::${pkg.id}`;
    setItems((prev) => prev.find((i) => i.key === key) ? prev : [...prev, {
      key, kind: "package", package_id: pkg.id, title: pkg.name, type: "digital",
      price: Number(pkg.price || 0), member_discount_pct: pkg.member_discount_pct || 0, qty: 1,
    }]);
    setOpen(true);
  }, []);
  const addTicket = useCallback((ev, opts = {}) => {
    const key = `tkt::${ev.id}::${opts.tier || ""}`;
    setItems((prev) => prev.find((i) => i.key === key) ? prev : [...prev, {
      key, kind: "ticket", event_id: ev.id,
      title: ev.title + (opts.tier ? ` — ${opts.tier}` : ""), type: "digital",
      price: Number(opts.price || 0), tier: opts.tier || "",
      meal_option: opts.meal_option || "", lodging_option: opts.lodging_option || "", qty: 1,
    }]);
    setOpen(true);
  }, []);
  const remove = useCallback((key) => setItems((prev) => prev.filter((i) => i.key !== key)), []);
  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((n, i) => n + i.qty, 0);

  return (
    <CartContext.Provider value={{ items, add, addPackage, addTicket, setQty, remove, clear, count, open, setOpen }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
